"use client";

import React, { useMemo } from "react";
import dynamic from "next/dynamic";
import { useSearchParams } from "next/navigation";
import { useEntityContextOptional } from "../contexts/EntityContextProvider";
import { FloatingModal } from "./FloatingModal";
import { useGlobalModalStack, MODAL_IDS, ResponsiveModal } from "@west-tennessee-consulting/ui";
import { useMediaQueryWithMount } from "@/lib/hooks/ui/use-media-query";
import type { AskMeridianModalData } from "@/lib/modals/data/modal-data-types";

/**
 * The chat itself (ChatPage + its providers, all from the meridian root
 * barrel) is loaded on demand: this container is statically imported by every
 * entity detail/list page but only mounted once the modal is opened, so the
 * chunk is fetched on first open instead of shipping with every page.
 */
const AskMeridianChatBody = dynamic(() => import("./AskMeridianChatBody"), {
  ssr: false,
  loading: () => <div className="h-full w-full" />,
});

/**
 * AskMeridianModal component
 * Modal that opens Meridian chat with entity context
 * Uses MeridianChatPage which handles all chat list management
 * Managed by global modal stack
 *
 * Entity context can come from two sources:
 * 1. EntityContextProvider (when viewing a detail page)
 * 2. Modal data passed from the askMeridian action (when clicking from table row)
 */
export function AskMeridianModalContainer() {
  const entityFromProvider = useEntityContextOptional();
  const searchParams = useSearchParams();
  const { getModalData, isModalOpen, closeModal } = useGlobalModalStack();
  const isOpen = isModalOpen(MODAL_IDS.ai.askMeridianEntity);
  const data = getModalData(MODAL_IDS.ai.askMeridianEntity) as AskMeridianModalData | undefined;
  const onClose = () => closeModal(MODAL_IDS.ai.askMeridianEntity);

  // Read chatId from URL query string to restore chat when modal opens
  const chatIdFromUrl = searchParams?.get("chatId") || undefined;

  const entityFromModalData = useMemo(() => {
    if (data && typeof data === "object") {
      const { entityType, entityId, entityData, entityDisplayName } = data;

      if (entityType && entityId && entityData) {
        return {
          entityType,
          entityId,
          entityData,
          entityDisplayName: entityDisplayName || "Unknown",
        };
      }
    }
    return null;
  }, [data]);

  // Use entity from modal data if available, otherwise use provider context
  const entity = entityFromModalData || entityFromProvider;

  // On mobile the draggable/resizable FloatingModal is a poor fit (a fixed
  // 600×700 box at hard-coded coordinates). Render the focused chat in a
  // near-full-height bottom drawer instead so the user gets a distraction-free
  // conversation. `mounted` gates the branch so we never flash the desktop
  // layout before the viewport is known (MOUNT_GATE_MUST_NOT_HIDE_DATA).
  const { matches: isMobile, mounted } = useMediaQueryWithMount("(max-width: 768px)");

  if (!entity) {
    return null;
  }

  const focusedChat = (
    <AskMeridianChatBody
      entityType={entity.entityType}
      entityId={entity.entityId}
      entityData={entity.entityData}
      entityDisplayName={entity.entityDisplayName}
      initialChatId={chatIdFromUrl}
    />
  );

  // Mobile: focused bottom drawer (open state is read from the global modal
  // stack via the shared id, so the same `ask-meridian-entity` open call drives
  // both surfaces — no second open path).
  if (mounted && isMobile) {
    return (
      <ResponsiveModal
        globalModalId={MODAL_IDS.ai.askMeridianEntity}
        side="bottom"
        title={`Ask Meridian: ${entity.entityDisplayName}`}
        className="flex flex-col"
      >
        <div className="h-full min-h-0 flex flex-col">{focusedChat}</div>
      </ResponsiveModal>
    );
  }

  // Desktop: draggable / resizable floating modal for multitasking.
  return (
    <FloatingModal
      isOpen={isOpen}
      onClose={onClose}
      title={`Ask Meridian: ${entity.entityDisplayName}`}
      initialPosition={{ x: 100, y: 100 }}
      initialSize={{ width: 600, height: 700 }}
    >
      {focusedChat}
    </FloatingModal>
  );
}
